import { flow, types, getEnv, Instance } from "mobx-state-tree";

import { JobInput } from "../../api-models/request/JobInput";
import { singleItemPatch } from "../api/ApiService";
import { currentUserService } from "../user/CurrentUserStorage";
import { JobResponse } from "../../api-models/response/job/JobResponse";
import { endpoints } from "../api/ApiEndpoints";
import { StorageModel } from "../storage/StorageModel";
import { JobStatus, JobType, JobPriority } from "../../generated/Jobs";

const emptyJob: JobResponse = {
  id: 0,
  name: "",
  project: {
    id: 0,
    name: "",
    isDeleted: false,
  },
  status: JobStatus.ToDo,
  type: JobType.DataGathering,
  assignedUser: {
    id: 0,
    name: "",
    isDeleted: false,
  },
  availableFrom: new Date(),
  deadline: new Date(),
  priority: JobPriority.Low,
  created: new Date(),
  createdBy: {
    id: 0,
    name: "",
    isDeleted: false,
  },
  description: "",
  sewerObjectsCount: 0
};

const CurrentJobModel = types.model({
  currentJobId: types.frozen<number>(),
  currentJob: types.optional(types.frozen<JobResponse>(), emptyJob),
});

export const CurrentJobStorageModel = types
  .compose(CurrentJobModel, StorageModel)
  .named("CurrentJobStorageModel")
  .actions((self) => {
    return {
      mapResponse(response: JobResponse) {
        self.currentJob = response;
      },
      editJobDetails: flow(function* (job: JobInput) {
        yield getEnv(self).api.put(self.endpoint, job);
        yield self.load();
      }),
      deleteJob: flow(function* () {
        yield getEnv(self).api.delete(self.endpoint);
      }),
      takeJob: flow(function* () {
        const endpoint = endpoints.get("user_job");
        endpoint.routeParams = [self.currentJobId];
        yield getEnv(self).api.put(endpoint);
        yield self.load();
      }),
      markJobDone: flow(function* () {
        const patchData: singleItemPatch = {
          op: "replace",
          path: "/status",
          value: JobStatus.Done,
        };
        yield getEnv(self).api.patch(self.endpoint, [patchData]);
        yield self.load();
      }),
      markJobInProgress: flow(function* () {
        const patchData: singleItemPatch = {
          op: "replace",
          path: "/status",
          value: JobStatus.InProgress,
        };
        yield getEnv(self).api.patch(self.endpoint, [patchData]);
        yield self.load();
      }),
    };
  })
  .views((self) => ({
    get canLoadItems() {
      return self.currentJobId > 0;
    },
    get endpoint() {
      const endpoint = endpoints.get("job");
      endpoint.routeParams = [self.currentJobId];
      return endpoint;
    },
    get isLoading() {
      return self.currentJob.id === 0;
    },
    get currentUserIsAssignedToJob(): boolean {
      return self.currentJob.assignedUser?.id === currentUserService.currentUser.id;
    },
  }));

export type CurrentJobStorage = Instance<typeof CurrentJobStorageModel>;
